/**
 * Cliente fino do tool `text-embeddings` do wuweiAgent.
 *
 * Modelo: `Xenova/multilingual-e5-small` (384d, multilíngue, ~118MB).
 * Espera prefixo de papel: "passage: <texto>" pra documentos indexados,
 * "query: <busca>" pra queries futuras. O tool não aplica esse prefixo
 * automaticamente — é responsabilidade deste cliente.
 *
 * Toda a inferência roda no worker do Agent (role "text"). Main thread
 * fica livre durante o embed.
 */

import { getPool, newInvocationId } from "./agent-pool.js";
import { recursoAtivo, motivoDesligado } from "./inferencia.js";
import * as llmNativo from "./llm-nativo.js";

const MODEL_NAME = "Xenova/multilingual-e5-small";

/** Versão do par modelo+pipeline. Bumpar quando trocar de modelo ou de
 *  estratégia de prefixo invalida embeddings antigos e força backfill. */
export const EMBEDDING_MODEL_ID = "multilingual-e5-small@1";
export const EMBEDDING_DIM = 384;

interface TextEmbeddingsInput {
  text: string;
  modelId?: string;
}
interface TextEmbeddingsOutput {
  vector: number[];
  norm: number;
  model: string;
  inputText: string;
  ms: number;
}

export type EmbeddingKind = "passage" | "query";

/**
 * Gera vetor 384d normalizado pra um texto. `kind` controla o prefixo:
 * - "passage" (default): documentos que vão ser indexados.
 * - "query": busca livre digitada pelo usuário.
 *
 * O modelo foi treinado com esses prefixos; não usar degrada qualidade.
 */
export async function embedText(
  text: string,
  kind: EmbeddingKind = "passage",
): Promise<Float32Array> {
  const trimmed = text.trim();
  if (!trimmed) throw new Error("texto vazio");
  const prefixed = `${kind}: ${trimmed}`;

  // O caminho nativo vem ANTES da chave de recurso, e isso não é descuido.
  //
  // A chave e os vereditos de "não cabe" foram medidos para OUTRO motor: o
  // ONNX dentro do WebAssembly da aba, onde tudo disputava o mesmo heap com
  // teto de 32 bits. Deixar um veredito sobre aquele motor desligar este seria
  // condenar o novo caminho pelo custo do antigo — que é exatamente o problema
  // que a migração existe para resolver.
  //
  // Foi o que aconteceu na primeira versão desta função: a chave nasce
  // desligada (`PADRAO = false` em inferencia.ts) e a bateria a desliga de novo
  // ao reprovar o recurso, então o motor nativo nunca chegava a ser chamado.
  if (llmNativo.garantirEmbeddings()) {
    const v = llmNativo.embedding(prefixed);
    // Confere a dimensão em vez de confiar: um GGUF de outro modelo carregado
    // por engano devolveria um vetor de outro tamanho, e comparar vetores de
    // dimensões diferentes não dá erro — dá resultado de busca sem sentido.
    if (v && v.length === EMBEDDING_DIM) return v;
  }

  // Daqui para baixo é o caminho antigo, em WebAssembly — e aí sim a chave
  // vale, porque é dela que aquele caminho fala.
  if (!recursoAtivo("busca")) throw new Error(motivoDesligado("busca"));

  const pool = getPool();
  const invId = newInvocationId("embed");
  const result = await pool.runTool<TextEmbeddingsInput, TextEmbeddingsOutput>(
    invId,
    "text-embeddings",
    { text: prefixed, modelId: MODEL_NAME },
  );
  return new Float32Array(result.vector);
}

export function embeddingsSupported(): boolean {
  // A ordem importa aqui pelo mesmo motivo de `embedText`: com motor nativo
  // disponível, a chave do caminho antigo não tem o que dizer. Só se PERGUNTA
  // se já está carregado — carregar 126 MB dentro de uma função chamada
  // "suportado?" seria efeito colateral escondido atrás de uma pergunta.
  if (llmNativo.embeddingsCarregado()) return true;
  if (!recursoAtivo("busca")) return false;
  return (
    typeof WebAssembly !== "undefined" && typeof navigator !== "undefined"
  );
}

/**
 * Cosine similarity. Saída do e5 já vem normalizada (norma 1), então em
 * teoria é puro dot product, mas calculamos a forma completa pra ficar
 * robusto contra vetores de outros modelos no futuro.
 */
export function cosineSim(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) return 0;
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    const av = a[i];
    const bv = b[i];
    dot += av * bv;
    na += av * av;
    nb += bv * bv;
  }
  if (na === 0 || nb === 0) return 0;
  return dot / Math.sqrt(na * nb);
}
